import {
    Box,
    DialogContent,
    TextField,
    Button,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { useState } from "react";
import IncidentClient, { IncidentErrors } from "../apiClients/IncidentClient";
import { useErrorDisplay } from "../components/ErrorDisplay"
import { GetUpsertIcon, IsEmpty, IsNotNullOrUndefined, IsNullOrUndefined } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { UpsertAction } from "../Enums";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import '@mui/lab';
import { AdapterDayjs as DateAdapter } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import { Talent, Incident } from "../models/dtos";
import TalentClient from "../apiClients/TalentClient";
import ConditionalDisplay from "../components/ConditionalDisplay";
import { MobileFormStack, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

export interface IncidentUpsertProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    inTalentId?: string;
    inTalent?: Talent;
    incidentId?: string;
    inIncident?: Incident;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    onReview?: (reviewed: boolean) => void;
    onClose?: () => void;
    icon?: JSX.Element;
}

const IncidentUpsertPopup = ({ mode, inTalentId, inTalent, incidentId, inIncident, onChange = async () => { }, onAdd = async () => { }, onReview = () => false, onClose = () => { }, icon = GetUpsertIcon(mode), ...btnProps }: IncidentUpsertProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);

    const [incident, setIncident] = useState<Incident>(null);
    const [talent, setTalent] = useState<Talent>(inTalent ?? null);
    const disableSave = mode === UpsertAction.View || IsEmpty(incident?.Type) || (mode === UpsertAction.Create && IsNullOrUndefined(talent));

    const title = () => {
        if (mode === UpsertAction.View) {
            return 'Review Event';
        }
        else {
            return (mode === UpsertAction.Create) ? 'Create Event' : `Edit Event`;
        }
    }

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                await createIncident();
            }
            else if (mode === UpsertAction.Update) {
                await new IncidentClient().Update(incident);
                await onChange();
            }

        } catch (error) {
            if (IsEmpty(error?.message))
                error.message = "Error Saving Event";

            setError(error, (mode === UpsertAction.Create) ? IncidentErrors.Create : IncidentErrors.Update);
        }
        finally {
            setBusy(false);
            setOpen(false);
            onClose();
        }
    };

    const handleReview = () => {
        setOpen(false);
        onReview(true);
        onClose();
    }

    const createIncident = async () => {

        let ci = {
            TalentId: talent.Id,
            Type: incident.Type,
            Title: incident.Title,
            Description: incident.Description,
            OccurrenceDate: incident.OccurrenceDate
        }

        let id = await new TalentClient().CreateIncident(talent.Id, ci);
        await onAdd(id);
    }

    const handleInputChange = e => {
        const { name, value } = e.target
        setIncident({ ...incident, [name]: value })
    };

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const handleClose = () => {
        setOpen(false);
        onClose();
    };

    const loadData = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                let resolvedTalent = talent;
                if (IsNullOrUndefined(resolvedTalent) && IsNotNullOrUndefined(inTalentId)) {
                    resolvedTalent = await new TalentClient().GetSingle(inTalentId);
                    setTalent(resolvedTalent);
                }

                if (IsNullOrUndefined(resolvedTalent)) {
                    throw new Error("Talent is required to create an event.");
                }

                var i = {
                    TalentId: resolvedTalent.Id,
                    Type: '',
                    Title: '',
                    Description: '',
                    OccurrenceDate: dayjs().format(),
                    Id: '',
                    IsDeleted: false,
                    LastUpdateDate: dayjs().format()
                };
                setIncident(i);
            }
            else if (mode === UpsertAction.Update) {
                if (IsNotNullOrUndefined(inIncident)) {
                    setIncident(inIncident);
                }
                // let p = await new IncidentClient().GetSingle(incidentId);
                // setIncident(p);
            } else {
                setIncident(inIncident);
            }

        } catch (error) {
            setError(error, IncidentErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    const formSpacing = 3;

    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title} onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog aria-labelledby="incident-upsert-title" open={open} fullWidth onClose={handleClose} tabletFullScreen="portrait" PaperProps={{ sx: { overflow: 'hidden', '& > div': { flex: '1 1 auto', minHeight: 0, height: 'auto !important', display: 'flex', flexDirection: 'column' } } }}>
            <BusySpinner busy={busy}>
                <ResponsiveDialogHeader id="incident-upsert-title" title={title()} onClose={handleClose} sx={{ margin: 0, padding: 2 }} />
                <DialogContent dividers sx={{ padding: 2, overflowY: 'auto' }}>
                    <MobileFormStack tabletColumns={2} desktopColumns={1} sx={{ gap: 0 }}>
                    <Box>
                        <InputLabel id="eventType-select-label">Type of Event: </InputLabel>
                        <Select fullWidth
                            labelId="eventType-select-label"
                            id="eventType-select"
                            name="Type"
                            disabled={mode === UpsertAction.View}
                            sx={{ marginBottom: formSpacing }}
                            error={IsEmpty(incident?.Type)}
                            value={incident?.Type ?? ''}
                            onChange={handleInputChange}>
                            <MenuItem value='Positive'>Positive</MenuItem>
                            <MenuItem value='Negative'>Negative</MenuItem>
                        </Select>
                    </Box>
                    <TextField label="Title" fullWidth
                        name="Title"
                        disabled={mode === UpsertAction.View}
                        sx={{ marginBottom: formSpacing }}
                        error={IsEmpty(incident?.Title)}
                        size='small'
                        variant="outlined"
                        value={incident?.Title}
                        onChange={handleInputChange} />
                    <TextField label="Description" fullWidth
                        name="Description"
                        disabled={mode === UpsertAction.View}
                        error={IsEmpty(incident?.Description)}
                        multiline={true}
                        placeholder="what happened?"
                        value={incident?.Description}
                        onChange={handleInputChange}
                        sx={{ marginBottom: formSpacing, gridColumn: { sm: '1 / -1', lg: 'auto' } }} />

                    <LocalizationProvider dateAdapter={DateAdapter}>
                        <DatePicker
                            label="Occurrence Date"
                            disabled={mode === UpsertAction.View}
                            value={incident?.OccurrenceDate}
                            maxDate={dayjs()}
                            onChange={(newValue) => setIncident({ ...incident, OccurrenceDate: dayjs(newValue).format() })}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </LocalizationProvider>
                    </MobileFormStack>
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: 1 }}>
                    <Button onClick={handleClose} variant='text' color='inherit'>Cancel</Button>
                    <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                        <Button onClick={handleReview} variant='outlined' color='primary'>Review Complete</Button>
                    </ConditionalDisplay>
                    <ConditionalDisplay ifTrue={mode === UpsertAction.Create || mode === UpsertAction.Update}>
                        <Button disabled={disableSave} onClick={handleSubmit} variant='outlined' color='primary'>Submit</Button>
                    </ConditionalDisplay>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
}

export default IncidentUpsertPopup;
